import React, { useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Camera, Upload, Sparkles, Loader2, RotateCcw } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BreedScanner({ open, onOpenChange }) {
  const { applyBalance } = useAuth();
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [base64, setBase64] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => {
    setPreview(null);
    setBase64(null);
    setResult(null);
    setLoading(false);
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setResult(null);
    const reader = new FileReader();
    reader.onload = () => {
      setPreview(reader.result);
      setBase64(String(reader.result).split(",")[1]);
    };
    reader.readAsDataURL(file);
  };

  const scan = async () => {
    if (!base64) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/breed-scan`, { image_base64: base64 });
      setResult(data);
      if (data?.points_balance != null) applyBalance(data.points_balance);
      toast.success(data?.points ? `Breed identified! +${data.points} pts 🏆` : "Breed identified! 🏆");
    } catch (err) {
      toast.error("Scan failed. Please try another photo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="max-w-[400px] rounded-3xl">
        <DialogHeader>
          <DialogTitle className="font-head text-xl font-extrabold flex items-center gap-2">
            <span className="text-2xl">📸</span> AI Breed Scanner
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-500 font-semibold -mt-1">
          Powered by Gemini Vision · results in seconds
        </p>

        <input ref={fileRef} type="file" accept="image/*" hidden onChange={onFile} />

        {!preview ? (
          <button
            onClick={() => fileRef.current?.click()}
            className="mt-2 w-full border-2 border-dashed border-slate-300 rounded-2xl py-10 flex flex-col items-center gap-2 text-slate-500 hover:border-[var(--paw-orange)] hover:text-[var(--paw-orange)] transition-colors"
          >
            <Upload className="w-8 h-8" />
            <span className="font-bold">Upload a photo of the stray</span>
            <span className="text-xs">JPG or PNG</span>
          </button>
        ) : (
          <div className="mt-2">
            <img loading="lazy" decoding="async" src={preview} alt="preview" className="w-full h-56 object-cover rounded-2xl" />
            {result && (
              <div className="mt-3 bg-violet-50 border border-violet-100 rounded-2xl p-4 fade-up">
                <div className="flex items-center gap-2 text-violet-700 font-extrabold">
                  <Sparkles className="w-4 h-4" /> {result.breed}
                </div>
                {result.confidence != null && (
                  <div className="text-xs font-bold text-violet-500 mt-0.5">
                    Confidence: {result.confidence}%
                  </div>
                )}
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{result.notes}</p>
              </div>
            )}
            <div className="flex gap-2 mt-3">
              <Button
                variant="outline"
                onClick={() => fileRef.current?.click()}
                className="h-11 rounded-2xl font-bold flex-1"
              >
                <RotateCcw className="w-4 h-4 mr-1" /> New photo
              </Button>
              <Button
                onClick={scan}
                disabled={loading}
                className="h-11 rounded-2xl bg-violet-600 hover:bg-violet-700 text-white font-extrabold flex-1"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Scanning…</>
                ) : (
                  <><Camera className="w-4 h-4 mr-1" /> {result ? "Scan again" : "Scan breed"}</>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
